import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import type * as React from "react";
import type { Ref } from "react";
import { Tick } from "../../icons/custom/tick";
import { cn } from "../../lib/utils";
const listVariants = cva("text-gray-700 dark:text-gray-100", {
  variants: {
    size: {
      sm: "text-sm [&>li]:mt-1",
      default: "text-base [&>li]:mt-2",
      lg: "text-lg [&>li]:mt-3"
    },
    variant: {
      unordered: "list-disc pl-5",
      ordered: "list-decimal pl-5",
      custom: "list-none pl-0 [&>li>ul]:ml-4"
    },
    color: {
      default: "text-gray-700 dark:text-gray-100",
      modal: "text-gray-700 dark:text-gray-200"
    }
  },
  defaultVariants: {
    size: "default",
    variant: "unordered",
    color: "default"
  }
});
export interface ListProps extends Omit<React.HTMLAttributes<HTMLUListElement | HTMLOListElement>, "color">, VariantProps<typeof listVariants> {
  ref?: Ref<HTMLUListElement | HTMLOListElement>;
}
const List = ({
  className,
  size,
  variant,
  color,
  ref,
  ...props
}: ListProps) => {
  const Component = variant === "ordered" ? "ol" : "ul";
  return <Component className={cn(listVariants({
    size,
    variant,
    color,
    className
  }))} ref={ref as React.Ref<HTMLUListElement & HTMLOListElement>} {...props} data-sentry-element="Component" data-sentry-component="List" data-sentry-source-file="list.tsx" />;
};
export interface ListItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
  customBullet?: React.ReactNode;
  ref?: Ref<HTMLLIElement>;
}
const ListItem = ({
  className,
  customBullet,
  children,
  ref,
  ...props
}: ListItemProps) => {
  const CustomBulletComp = typeof customBullet === "string" ? "span" : SlotPrimitive.Slot;
  return <li className={className} ref={ref} {...props} data-sentry-component="ListItem" data-sentry-source-file="list.tsx">
      {!!customBullet && <CustomBulletComp className="mr-2 shrink-0">
          {customBullet}
        </CustomBulletComp>}
      {children}
    </li>;
};
interface ListItemTickProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: Ref<HTMLSpanElement>;
}
const ListItemTick = ({
  className,
  ref,
  ...props
}: ListItemTickProps) => <span className={cn("inline-flex aspect-square items-center justify-center rounded-[0.375em] bg-green text-white [height:calc(1em_*_1.25)] [margin-top:calc((1em_*_var(--line-height,_1.5)_-_(1em_*_1.25))_/_2)] [width:calc(1em_*_1.25)]", className)} ref={ref} {...props} data-sentry-component="ListItemTick" data-sentry-source-file="list.tsx">
    <Tick className="[height:calc(1em_*_0.75)] [width:calc(1em_*_0.75)]" data-sentry-element="Tick" data-sentry-source-file="list.tsx" />
  </span>;
export { List, ListItem, ListItemTick, listVariants };